import { useEffect, useRef, useState } from 'react';
import { FRESH_MS, loadShared, readCache } from './dashboardCache';

/**
 * One Warning Dashboard request, remembered (see dashboardCache.js).
 *
 *   const { data, error, loading, updatedAt } = useDashboardRequest(url, reloadKey);
 *
 *  - On the very first render the remembered answer for `url` is already in `data` (no spinner, no flash of empty).
 *  - A fresh answer (younger than `freshMs`) triggers no request; an older one is refreshed in the background while the
 *    old numbers stay on screen; none at all loads normally (`loading` is true until something arrives).
 *  - `reloadKey` going up (a Retry or the Refresh button) always asks the server again.
 *  - A failed refresh never throws away numbers already on screen; `error` is only set when there is nothing to show.
 *  - Changing `url` (another month) switches to that URL's remembered answer at once, if there is one.
 */
export const useDashboardRequest = (url, reloadKey = 0, { freshMs = FRESH_MS } = {}) => {
  const [mine, setMine] = useState(null); // { url, data, fetchedAt } - the last answer THIS component received
  const [failure, setFailure] = useState(null); // { url, message } - the last failed load
  const handledKey = useRef(reloadKey);

  // read on every render: the newest answer anyone has stored for this url, else the one this component got itself
  // (the shared store may be switched off - then the component still keeps what it just loaded)
  const entry = readCache(url, freshMs) || (mine && mine.url === url ? { data: mine.data, fetchedAt: mine.fetchedAt } : null);

  useEffect(() => {
    const forced = handledKey.current !== reloadKey;
    handledKey.current = reloadKey;
    const known = readCache(url, freshMs);
    if (known && known.fresh && !forced) return undefined; // recent enough - nothing to ask

    let cancelled = false;
    setFailure(null); // a card that has nothing to show goes back to "loading" while it retries
    loadShared(url)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'success') {
          setFailure(null);
          setMine({ url, data: res.data, fetchedAt: Date.now() });
        } else {
          setFailure({ url, message: res?.message || 'Could not load' });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setFailure({ url, message: err?.response?.data?.message || 'Could not load' });
      });
    return () => {
      cancelled = true;
    };
    // freshMs is a constant per call site
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, reloadKey]);

  const failed = failure && failure.url === url ? failure : null;
  return {
    data: entry ? entry.data : null,
    updatedAt: entry ? entry.fetchedAt : null,
    error: !entry && failed ? failed.message : null,
    loading: !entry && !failed,
  };
};

export default useDashboardRequest;
